// Planificación PURA de las notificaciones locales que se agendan por
// adelantado: los tips del día y los recordatorios de turno. Aquí vive el QUÉ
// y el CUÁNDO; `lib/notifications.js` sólo se lo entrega al sistema operativo.
// Sin Capacitor, sin localStorage: por eso se puede testear.
//
// EL PORQUÉ DEL HORIZONTE CORTO (2026-09-04, avalancha reportada por el
// fundador: once notificaciones apiladas, todas con hora "Ahora").
// Una notificación local es una ALARMA del sistema operativo, y la app no pide
// permiso de alarma exacta (no corresponde: no somos un despertador, y Google
// Play lo restringe). Sin ese permiso, en Android 12+ el plugin cae a una
// alarma INEXACTA, que el sistema puede posponer mientras el teléfono está en
// ahorro de batería o dormido, y entregar TODAS JUNTAS al despertar. Antes se
// dejaban hasta 30 tips (10 días x 3) y 14 avisos de turno esperando en la
// cola del sistema: un teléfono al 9% de batería acumuló varios días y los
// descargó de una.
//
// El sistema decide CUÁNDO entrega. Nosotros decidimos CUÁNTAS puede llegar a
// acumular, y esa es la única palanca real: con un tip al día y dos días de
// horizonte, el peor caso son dos notificaciones juntas en vez de treinta.
// La app reprograma desde cero cada vez que se abre, así que quien la usa
// nunca se queda sin; a quien no la abre hace días, no se le hostiga.
//
// No "arreglar" esto pidiendo SCHEDULE_EXACT_ALARM: el problema no es la
// puntualidad de cada aviso, es la profundidad de la cola.
import { calcularTurnoDelDia } from '../utils/calendario';

// Horizonte de programación, en días (hoy incluido).
export const TIPS_DIAS = 2;
export const TURNO_DIAS = 3;

// Un solo tip al día, a media mañana. La hora se mueve con un jitter
// determinístico para que no se sienta robótico.
export const TIPS_HORA = 10;

export const TIPS = [
  { title: '¿Ya recibiste a tu paciente?', body: 'Hazle el examen físico y regístralo. De cabeza a pies, sin saltarte nada.' },
  { title: '¿Vas a preparar un medicamento?', body: 'Acuérdate de los 10 correctos antes de administrar.' },
  { title: 'Higiene de manos', body: 'Los 5 momentos: antes y después de tu paciente.' },
  { title: 'Revisa tus accesos', body: 'CVC: que refluya e infunda, técnica push stop, sin acodaduras.' },
  { title: '¿Tomaste el HGT?', body: 'Si tu paciente lo necesita, contrólalo y anótalo.' },
  { title: 'Ojo con la diuresis', body: 'Vigila el ritmo: oliguria es menos de 0,5 ml/kg/h. Avisa si baja.' },
  { title: 'Antes del procedimiento', body: 'Verifica identidad, consentimiento, materiales y posición.' },
  { title: '¿Tu paciente con dolor?', body: 'Objetívalo con EVA, o CPOT si no comunica, y registra el manejo.' },
  { title: 'Cierra tu turno ordenado', body: 'Arma la evolución por sistema en MiPAE y compártela.' },
  { title: 'Sondas y drenajes', body: '¿Permeables, bien fijados y cuantificados? Revísalos.' },
  { title: '¿Paciente sedado?', body: 'Monitorea la profundidad, RASS o SAS, y evalúa el despertar diario.' },
  { title: 'Signos de alarma', body: 'Ten claro qué vigilar y cuándo escalar este turno.' },
  { title: 'Balance hídrico', body: 'Suma ingresos y egresos. Un balance bien llevado vale oro.' },
  // 'Tu bitácora · ¿Algo te marcó hoy? Anótalo' se RETIRÓ el 2026-09-24: el
  // autocuidado no se empuja (regla del fundador, misma decisión que retiró el
  // nudge turno_pasado_sin_cerrar). Bienestar y la Bitácora están si la persona
  // los busca, jamás como aviso. No restaurarlo.
];

const TIPO_TURNO = { largo: 'largo', noche: 'de noche', '24': 'de 24h', diurno: 'diurno' };

// Jitter determinístico por día: minutos −45..+44. Estable si se reprograma el
// mismo día (no mueve el aviso de un lado a otro), pero distinto cada día.
export function jitterMin(seed) {
  const x = Math.sin(seed * 99991) * 10000;
  return Math.floor((x - Math.floor(x)) * 90) - 45;
}

function medianoche(ahora, sumaDias) {
  const d = new Date(ahora);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + sumaDias);
  return d;
}

export function fechaConHora(base, hhmm) {
  const [h, m] = String(hhmm).split(':').map(Number);
  const d = new Date(base);
  d.setHours(h, m || 0, 0, 0);
  return d;
}

// Un tip al día durante los próximos TIPS_DIAS días, sólo si la hora todavía
// no pasó. Devuelve [{ at, title, body }] en orden cronológico.
export function planTips({ ahora = new Date(), margenMs = 60 * 1000 } = {}) {
  const t0 = ahora.getTime();
  const diaEpoch = Math.floor(t0 / 86400000); // varía el tip día a día
  const plan = [];

  for (let i = 0; i < TIPS_DIAS; i++) {
    const cuando = medianoche(ahora, i);
    cuando.setHours(TIPS_HORA, 0, 0, 0);
    cuando.setMinutes(cuando.getMinutes() + jitterMin(diaEpoch + i));
    if (cuando.getTime() <= t0 + margenMs) continue;
    const tip = TIPS[(diaEpoch + i) % TIPS.length];
    plan.push({ at: cuando, title: tip.title, body: tip.body });
  }
  return plan;
}

// Recordatorios de turno de los próximos TURNO_DIAS días: uno 1 h antes de
// empezar y otro 30 min después del fin. Devuelve [{ at, title, body }].
export function planTurno({ ahora = new Date(), rotacion, margenMs = 60 * 1000 } = {}) {
  if (!rotacion?.patron) return [];
  const t0 = ahora.getTime();
  const plan = [];

  for (let i = 0; i < TURNO_DIAS; i++) {
    const dia = medianoche(ahora, i);
    const t = calcularTurnoDelDia(dia, rotacion);
    if (t.tipo === 'libre' || !t.horaInicio) continue;

    // Pre turno: 1 hora antes del inicio.
    const preAt = new Date(fechaConHora(dia, t.horaInicio).getTime() - 60 * 60 * 1000);
    if (preAt.getTime() > t0 + margenMs) {
      const etiqueta = TIPO_TURNO[t.tipo];
      plan.push({
        at: preAt,
        title: 'Tu turno parte pronto',
        body: `Turno${etiqueta ? ` ${etiqueta}` : ''} a las ${t.horaInicio}. Prepáralo en MiPAE`,
      });
    }

    // Post turno: 30 min después del fin (ojo con los que cruzan medianoche).
    if (t.horaFin) {
      const baseFin = t.terminaSiguienteDia ? medianoche(ahora, i + 1) : dia;
      const postAt = new Date(fechaConHora(baseFin, t.horaFin).getTime() + 30 * 60 * 1000);
      if (postAt.getTime() > t0 + margenMs) {
        plan.push({
          at: postAt,
          title: 'Cierra tu turno',
          body: 'Mira tu día y descárgalo en 2 minutos.',
        });
      }
    }
  }
  return plan;
}
